import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import ScrollReveal from '../components/ScrollReveal';
import AnimatedBackground from '../components/AnimatedBackground';
import { images } from '../data/images';
import { AboutVisual } from '../components/AboutVisual';
import { Target, Shield, Users, Globe, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const AboutPage = () => {
  const { t } = useLanguage();

  const valueIcons = [Target, Shield, Users];

  const handleNavClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-[#1E2A38] overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${images.about.connectivity})` }}
        />
        <AnimatedBackground className="opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1E2A38]/50 to-[#1E2A38]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t('about.title')}
            </h1>
            <div className="w-24 h-1 bg-[#0066CC] rounded-full" />
          </ScrollReveal>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <ScrollReveal direction="right">
              <div>
                <h2 className="text-3xl font-bold text-[#2B3A4D] mb-8">
                  {t('about.whoWeAre.title')}
                </h2>
                <div className="space-y-6">
                  {t('about.whoWeAre.content').split('\n\n').map((paragraph, index) => (
                    <p key={index} className="text-[#5C5C5C] leading-relaxed text-lg">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </ScrollReveal>
            
            {/* Visual Element */}
            <ScrollReveal direction="left" delay={200}>
              <div className="relative">
                <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-[#1E2A38] via-[#2B3A4D] to-[#0066CC]/20">
                  {/* SVG Illustration */}
                  <div className="w-full h-full">
                    <AboutVisual />
                  </div>
                  
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#2B3A4D]/40 via-transparent to-[#0066CC]/10" />
                  
                  {/* Decorative Elements */}
                  <div className="absolute top-6 right-6 w-20 h-0.5 bg-[#0066CC] opacity-80" />
                  <div className="absolute bottom-6 left-6 w-20 h-0.5 bg-[#0066CC] opacity-80" />
                </div>
                
                {/* Floating Badge */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 border border-[#E5E7EB]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#0066CC]/10 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-[#0066CC]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#6B7B8C] uppercase tracking-wider">Part of</p>
                      <p className="text-base font-semibold text-[#2B3A4D]">Global Services s.r.o.</p>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl font-bold text-[#2B3A4D] text-center mb-16">
              {t('about.values.title')}
            </h2>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t('about.values.items').map((value, index) => {
              const IconComponent = valueIcons[index];
              return (
                <ScrollReveal key={index} delay={index * 150}>
                  <div 
                    className="bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300 group hover:-translate-y-2 border border-transparent hover:border-[#0066CC]/20"
                  >
                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#0066CC]/10 flex items-center justify-center group-hover:bg-[#0066CC] transition-all duration-300 group-hover:scale-110">
                      <IconComponent className="w-8 h-8 text-[#0066CC] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#2B3A4D] mb-3">
                      {value.title}
                    </h3>
                    <p className="text-[#6B7B8C] leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* International Group Section */}
      <section className="py-24 bg-[#2B3A4D] relative overflow-hidden">
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url(${images.about.connectivity})` }}
        />
        <AnimatedBackground className="opacity-30" />
        
        <ScrollReveal>
          <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-3 mb-8">
              <div className="w-12 h-0.5 bg-[#0066CC]" />
              <span className="text-[#0066CC] text-sm font-medium uppercase tracking-wider">International Group</span>
              <div className="w-12 h-0.5 bg-[#0066CC]" />
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
              Global Services s.r.o.
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              As a wholly owned subsidiary of Global Services s.r.o., HITECH GROUP benefits from international technological capabilities while maintaining local French expertise and client-focused service delivery.
            </p>
            
            <Link to="/contact" onClick={handleNavClick}>
              <Button 
                size="lg"
                className="bg-[#0066CC] hover:bg-[#0055AA] text-white group transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#0066CC]/30"
              >
                Partner With Us
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
              </Button>
            </Link>
          </div>
        </ScrollReveal>
      </section>
    </div>
  );
};

export default AboutPage;
